import { useState } from "react";
import { Check } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { getLocalPrefs, savePrefs } from "@/lib/prefs";

const LANGS = [{ c: "ru", n: "Русский" }, { c: "en", n: "English" }, { c: "kk", n: "Қазақша" }];
const DIETS = ["halal", "vegetarian", "vegan", "lactose"];

export const Onboarding = () => {
  const { t, lang, setLang } = useI18n();
  const { user, setUserPrefs } = useAuth();
  const [done, setDone] = useState(() => !!localStorage.getItem("nutriscan_onboarded"));
  const [step, setStep] = useState(1);
  const [sel, setSel] = useState(localStorage.getItem("nutriscan_lang") || "ru");
  const [diets, setDiets] = useState(() => getLocalPrefs().diets || []);

  if (done) return null;

  const chooseLang = (c) => { setSel(c); setLang(c); };
  const toggleDiet = (d) => setDiets((p) => p.includes(d) ? p.filter((x) => x !== d) : [...p, d]);

  const finish = async (savedDiets) => {
    try {
      const merged = await savePrefs({ ...getLocalPrefs(), diets: savedDiets, language: sel }, !!user);
      if (user) setUserPrefs(merged);
    } catch { /* keep local */ }
    localStorage.setItem("nutriscan_onboarded", "1");
    setDone(true);
  };

  return (
    <div className="fixed inset-0 z-[120] flex flex-col items-center justify-center bg-white px-8" data-testid="onboarding">
      {step === 1 && (
        <div className="w-full max-w-xs flex flex-col items-center animate-in fade-in duration-300" data-testid="onboarding-step-1">
          <img src="/logo-mark.png" alt="NutriScan" className="w-20 h-20 rounded-3xl mb-6" />
          <h2 className="font-heading font-extrabold text-2xl text-slate-900 mb-6 text-center">{t("onb.sub")}</h2>
          <div className="w-full space-y-3">
            {LANGS.map((l) => (
              <button key={l.c} onClick={() => chooseLang(l.c)} data-testid={`onb-lang-${l.c}`}
                className={`w-full h-14 rounded-2xl font-semibold border-2 flex items-center justify-between px-5 transition-colors ${sel === l.c ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-200"}`}>
                {l.n}{sel === l.c && <Check className="w-5 h-5" />}
              </button>
            ))}
          </div>
          <button onClick={() => setStep(2)} data-testid="onb-continue"
            className="w-full mt-8 h-14 rounded-2xl bg-primary text-white font-bold active:scale-95 transition-transform">
            {t("onb.continue")}
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="w-full max-w-xs flex flex-col items-center animate-in fade-in slide-in-from-right-4 duration-300" data-testid="onboarding-step-2">
          <h2 className="font-heading font-extrabold text-2xl text-slate-900 mb-1 text-center">{t("onb.prefsTitle")}</h2>
          <p className="text-sm text-slate-400 mb-6 text-center">{t("onb.prefsSub")}</p>
          <div className="w-full space-y-2.5 max-h-[46vh] overflow-y-auto">
            {DIETS.map((d) => {
              const on = diets.includes(d);
              return (
                <button key={d} onClick={() => toggleDiet(d)} data-testid={`onb-diet-${d}`}
                  className={`w-full h-14 rounded-2xl font-semibold border-2 flex items-center justify-between px-5 transition-colors ${on ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-200"}`}>
                  <span>{t(`diet.${d}`)}</span>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${on ? "bg-white border-white" : "border-slate-300"}`}>
                    {on && <Check className="w-4 h-4 text-emerald-600" strokeWidth={3} />}
                  </span>
                </button>
              );
            })}
          </div>
          <button onClick={() => finish(diets)} data-testid="onb-finish"
            className="w-full mt-6 h-14 rounded-2xl bg-primary text-white font-bold active:scale-95 transition-transform">
            {t("onb.continue")}
          </button>
          <button onClick={() => finish([])} data-testid="onb-skip"
            className="mt-3 text-sm font-semibold text-slate-400 active:scale-95 transition-transform">
            {t("onb.skip")}
          </button>
        </div>
      )}
    </div>
  );
};
